/** Original inline SVG icons (40x40, currentColor). Heat badges, one per wanted level. */

const svg = (inner: string): string =>
  `<svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;

export const HEAT_ICONS: readonly string[] = [
  // 0 — Unnoticed: plain shield, a dash through it
  svg('<path d="M20 4.5 33.5 9.5V20c0 7.6-5.6 13.2-13.5 16C12.1 33.2 6.5 27.6 6.5 20V9.5Z"/><path d="M13.5 20.5h13"/>'),
  // 1 — Suspected: custodian helmet
  svg(
    '<path d="M9.5 25c0-10.5 4.4-17.2 10.5-17.2S30.5 14.500 30.500 25Z" fill="currentColor" fill-opacity=".22"/><path d="M5 25h30v4.500H5z" fill="currentColor"/><circle cx="20" cy="5.600" r="2.200" fill="currentColor"/><path d="M16 17.500h8M20 14v7"/>',
  ),
  // 2 — In pursuit: siren lamp with rays
  svg(
    '<path d="M11 28V20a9 9 0 0 1 18 0v8Z" fill="currentColor" fill-opacity=".3"/><path d="M8 28h24v5H8z" fill="currentColor"/><path d="M20 5v4M7 10l3 3M33 10l-3 3M3.500 20H8M32 20h4.500"/>',
  ),
  // 3 — Roadblocks: striped barrier on posts
  svg(
    '<path d="M7 33V15M33 33V15"/><path d="M4 11h32v9H4z" fill="currentColor" fill-opacity=".25"/><path d="M9 20l6-9M17 20l6-9M25 20l6-9"/><path d="M13 33h14"/>',
  ),
  // 4 — City-wide: starburst with a watching eye
  svg(
    '<path d="m20 3.500 3.400 7.400 7.900-2.400-2.300 8 7.700 3.200-7.300 4.400 2.900 7.900-8-1.500L20 37l-4.300-7.500-8 1.500 2.900-7.900L3.300 19.100 11 15.900l-2.300-8 7.900 2.400Z" fill="currentColor" fill-opacity=".22"/><circle cx="20" cy="20" r="4.500" fill="currentColor"/>',
  ),
];

/** 24x24 line icons for the touch controls and the HUD instruments (currentColor, original drawings). */
const line = (inner: string): string =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;

export type ControlIcon = 'aim' | 'swap' | 'run' | 'use' | 'gas' | 'brake' | 'handbrake' | 'out' | 'car' | 'horn' | 'radio' | 'map' | 'pause' | 'menu' | 'warn' | 'fire' | 'book' | 'pager';

export const CONTROL_ICONS: Record<ControlIcon, string> = {
  // gunsight: ring, four ticks, a dot
  aim: line('<circle cx="12" cy="12" r="6.5"/><path d="M12 2.5v4M12 17.5v4M2.5 12h4M17.5 12h4"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/>'),
  // two arrows chasing round
  swap: line('<path d="M5 9.5a7 7 0 0 1 12.2-3.2L19 8"/><path d="M19 3.5V8h-4.5"/><path d="M19 14.5a7 7 0 0 1-12.2 3.2L5 16"/><path d="M5 20.5V16h4.5"/>'),
  // running figure
  run: line('<circle cx="15" cy="4.2" r="1.9" fill="currentColor"/><path d="M8 8.5l4-1.5 3 2.5 3 1"/><path d="M12 7l-2 6 4 3-1 5"/><path d="M10 13l-3 3H3.5"/>'),
  // open hand, palm out
  use: line(
    '<path d="M8 12V5.5a1.5 1.5 0 0 1 3 0V11"/><path d="M11 10.5V4a1.5 1.5 0 0 1 3 0v6.5"/><path d="M14 10.5V5.5a1.5 1.5 0 0 1 3 0V13"/><path d="M8 12l-1.6-1.8a1.6 1.6 0 0 0-2.4 2.1L7.5 18a5 5 0 0 0 4 2.2H13a4 4 0 0 0 4-4V13"/>',
  ),
  // accelerator: a tall ribbed pad over its hinge
  gas: line('<rect x="7.5" y="2.5" width="9" height="16" rx="2"/><path d="M10 6.5h4M10 9.5h4M10 12.5h4M10 15.5h4"/><path d="M9 21.5h6"/>'),
  // brake: a wide ribbed pad
  brake: line('<rect x="3" y="7" width="18" height="10" rx="2.2"/><path d="M7 10v4M10.3 10v4M13.6 10v4M17 10v4"/>'),
  // handbrake lever: pivot, shaft and grip
  handbrake: line('<circle cx="5.5" cy="18.5" r="2"/><path d="M7 17l9.5-9.5"/><path d="M15 9l3.5-3.5a2 2 0 0 1 2.8 2.8L17.8 11.8"/><path d="M3 22h7"/>'),
  // door with an arrow stepping out of it
  // a saloon side-on: body, roof, two wheels
  car: line('<path d="M3.5 15.5v-3.2l2.2-1 1.8-3.8h8.6l2.4 3.8 2 1v3.2z"/><circle cx="7.5" cy="16" r="1.9"/><circle cx="16.5" cy="16" r="1.9"/><path d="M8.3 11.3h7.6"/>'),
  out: line('<path d="M13 3.5H6.5v17H13"/><path d="M10.5 12h10M17 8.5l3.5 3.5-3.5 3.5"/>'),
  // bulb horn
  horn: line('<path d="M3 10v4h3l7 5V5L6 10Z"/><path d="M16.5 9a4 4 0 0 1 0 6M19 6.5a7.5 7.5 0 0 1 0 11"/>'),
  // wireless set: cabinet, dial, speaker cloth, aerial
  radio: line('<rect x="3" y="8" width="18" height="12" rx="2"/><circle cx="15.5" cy="14" r="3"/><path d="M6 12h4M6 15h4M6 18h4"/><path d="M7 8l9-5"/>'),
  // folded street map
  map: line('<path d="M3 6l6-2.5 6 2.5 6-2.5v14.5l-6 2.5-6-2.5-6 2.5Z"/><path d="M9 3.5V18M15 6v14.5"/>'),
  pause: line('<path d="M8.5 5v14M15.5 5v14" stroke-width="3.2"/>'),
  menu: line('<path d="M4 6.5h16M4 12h16M4 17.5h16"/>'),
  // warning lamp: triangle with a bang
  warn: line('<path d="M12 3.5 21.5 20h-19Z"/><path d="M12 9.5v5"/><circle cx="12" cy="17.2" r=".9" fill="currentColor"/>'),
  // fire lamp
  fire: line('<path d="M12 21c-4 0-6.5-2.6-6.5-6 0-3.6 3-5.6 3.6-9.5 2.4 1.5 3.4 3.6 3.2 5.6 1.2-.6 1.9-1.7 2.1-3 2.2 1.9 3.6 4.3 3.6 6.9 0 3.4-2.5 6-6 6Z"/>'),
  // the little black book: a bound notebook with an elastic strap and a thumb-index tab
  book: line('<path d="M6 3.5h11.5a1.5 1.5 0 0 1 1.5 1.5v14a1.5 1.5 0 0 1-1.5 1.5H6Z" fill="currentColor" fill-opacity=".22"/><path d="M6 3.5v17M8.5 3.5v17"/><path d="M15 3.5v17" stroke-width="1.4"/><path d="M19 7.5h2v3h-2"/>'),
  // a bleeper: a pocket pager with its little window and clip
  pager: line('<rect x="3.5" y="6.5" width="17" height="11" rx="2"/><rect x="6" y="9" width="8.5" height="4" rx=".6" fill="currentColor" fill-opacity=".3"/><circle cx="17.5" cy="11" r="1.1" fill="currentColor"/><path d="M8 17.5v2M16 17.5v2"/>'),
};

/** Weapon silhouettes (60x36, filled with currentColor): the weapon HUD plate and the touch HIT button. */
const gun = (d: string): string => `<svg viewBox="0 0 60 36" aria-hidden="true"><path fill="currentColor" d="${d}"/></svg>`;
/**
 * A silhouette from several parts, each its own path so overlapping parts simply merge; a part's own
 * inner rings (even-odd) are cut out of it: the cylinder flutes, the trigger-guard bow.
 */
const parts = (...ds: string[]): string =>
  `<svg viewBox="0 0 60 36" aria-hidden="true"><g fill="currentColor" fill-rule="evenodd">${ds.map((d) => `<path d="${d}"/>`).join('')}</g></svg>`;

/**
 * The .38 (docs/WEAPONS.md, "Weapon models"): a period revolver, muzzle right. Cylinder taller than the
 * frame with two flutes showing, top strap running on into a thin barrel with a ramp sight and the
 * ejector-rod shroud under it, a spur hammer cocked back over a round, curved butt, an open trigger
 * guard with the trigger in it. Read at ~40-56 px wide (HUD plate, touch HIT button).
 */
const REVOLVER = parts(
  // top strap running on into the barrel (one flat top line), ramp front sight, ejector-rod shroud
  'M18 8H54.4V11.6H18Z',
  'M50.2 8.2 52.9 6.2H54.4V8.2Z',
  'M34.6 11.2H48.4V13.8H34.6Z',
  // rear frame
  'M15.8 8H22V21.6H15.8Z',
  // spur hammer, cocked back over the butt
  'M17.8 8.6 13.4 6C12.6 5.6 11.9 6.4 12.5 7.1L15.8 10.4Z',
  // cylinder, deep below the barrel line, with two flutes
  'M23.4 9.4H32.6A1.8 1.8 0 0 1 34.4 11.2V20.4A1.8 1.8 0 0 1 32.6 22.2H23.4A1.8 1.8 0 0 1 21.6 20.4V11.2A1.8 1.8 0 0 1 23.4 9.4Z' +
    'M23.8 13H32.2V15H23.8Z M23.8 17.2H32.2V19.2H23.8Z',
  // trigger guard bow and trigger
  'M19 21H28.4V22.2C28.4 26 26.1 28 23.4 28S19 26 19 23.4Z M20.9 22.2H26.5C26.5 25 25.2 26.3 23.5 26.3S20.9 25 20.9 23.3Z',
  'M22.6 21.8H24.2L23.6 25.4H22.3Z',
  // round walnut butt, curving down and back
  'M15.8 20H21.4C19.9 22.8 18.9 26 18.7 29.6 18.5 32.6 16.4 34 13.6 33.8 11.1 33.6 9.7 31.8 10.3 29.4 11.1 26.1 12.9 22.9 15.8 20Z',
);

/**
 * The sawn-off: two tubes side by side (the seam between them shows), stubby and thick, a squared
 * action with its top lever, a splinter fore-end, two triggers, and a short pistol grip ending in the
 * flat saw cut where the butt was taken off. No cylinder, no hammer spur: unmistakable beside the .38.
 */
const SAWNOFF = parts(
  // barrels with the seam between the two tubes, the muzzle a touch proud
  'M25.4 10.2H53.6V18H25.4Z M27.4 13.4H51.8V14.8H27.4Z',
  'M52.8 9.6H55.4V18.6H52.8Z',
  // splinter fore-end
  'M28.4 17.6H42.4L40.8 20.6H29.8Z',
  // action, hinge knuckle, top lever
  'M17 9.8H26.6V20.6H17Z',
  'M24.6 19.2A2 2 0 1 0 28.6 19.2A2 2 0 1 0 24.6 19.2Z',
  'M17.6 10 21.2 7.2H23.6L22.2 10Z',
  // trigger guard and two triggers
  'M18.4 20.2H26.4V21.6C26.4 25.2 24.4 27 21.9 27S18.4 25.2 18.4 22.8Z M20.1 21.6H24.7C24.7 24.2 23.6 25.4 22 25.4S20.1 24.2 20.1 22.6Z',
  'M20.9 21.2H22.1V24.4H20.9Z M23 21.2H24.2V23.8H23Z',
  // short pistol grip, sawn off flat
  'M17.6 11.4 5.6 19.4 9 27.2 15.6 23.2 18.4 20.6Z',
);

export const WEAPON_ICONS: Readonly<Record<string, string>> = {
  unarmed: gun('M18 12h22a5 5 0 0 1 5 5v7a7 7 0 0 1-7 7H24a8 8 0 0 1-8-8v-6a5 5 0 0 1 2-5z M20 8h5v6h-5z M27 7h5v7h-5z M34 8h5v6h-5z'),
  blunt: gun('M6 22l30-8 3 6-30 8z M38 13l14-4 3 9-14 4z'),
  cosh: gun('M8 20l22-4 1 4-22 4z M30 13c6-3 16-2 18 4s-6 9-12 8-9-9-6-12z'),
  bat: gun('M4 20l14-3 1 3-14 3z M18 15l34-7 3 10-34 7z'),
  crowbar: gun('M6 24l40-10 1 3-40 10z M46 14l6-8 3 2-6 9z'),
  blade: gun('M8 21l16-4 1 5-16 4z M26 16l26-8-2 6-23 7z'),
  revolver: REVOLVER,
  sawnoff: SAWNOFF,
  // Class fallbacks: the only pistol is the .38, the only shotgun the sawn-off.
  pistol: REVOLVER,
  shotgun: SAWNOFF,
};

/** The silhouette for a weapon: its own drawing where it has one (bat, crowbar, cosh, .38, sawn-off), else its class. */
export function weaponIcon(id: string, cls: string): string {
  return WEAPON_ICONS[id] ?? WEAPON_ICONS[cls] ?? WEAPON_ICONS.unarmed!;
}
